import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Login } from './components/Login';
import { RegisterPage } from './pages/RegisterPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { PaymentCallbackPage } from './pages/PaymentCallbackPage';
import { ForgotPassword } from './components/ForgotPassword';
import { BoardPage } from './components/BoardPage';
import { BoardAdmin } from './components/BoardAdmin';
import { JoinBoard } from './pages/JoinBoard';
import { ArchivedCards } from './pages/ArchivedCards';
import { ProfileSettings } from './pages/ProfileSettings';
import { AccountManagementPage } from './pages/AccountManagementPage';
import { UpgradePlanPage } from './pages/UpgradePlanPage';
import { LandingPage } from './components/LandingPage';
import AdminDashboard from './components/AdminDashboard';
import TenantDetail from './components/TenantDetail';
import UserAdminDashboard from './components/UserAdminDashboard';
import UserDetail from './components/UserDetail';
import SubscriptionPlansConfig from './components/SubscriptionPlansConfig';
import AdminExtendedDashboard from './components/AdminExtendedDashboard';
import ApiTokensManagement from './components/ApiTokensManagement';
import ApiDocumentation from './pages/ApiDocumentation';
import UserDocumentation from './pages/UserDocumentation';
import { LegalPage } from './pages/LegalPage';
import { CookieBanner } from './components/CookieBanner';
import { SuperAdminRoute } from './components/SuperAdminRoute';
import './index.css';
import './styles/ads.css';

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        Chargement...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Vérifier si l'email est vérifié
  if (user && !user.email_verified_at) {
    return <Navigate to="/verify-email" state={{ email: user.email }} />;
  }

  return <>{children}</>;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-kanban-blue">
        <div className="text-white text-2xl">Chargement...</div>
      </div>
    );
  }

  return !isAuthenticated ? <>{children}</> : <Navigate to="/dashboard" />;
};

const HomeRoute: React.FC = () => {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-kanban-blue">
        <div className="text-white text-2xl">Chargement...</div>
      </div>
    );
  }

  return <LandingPage />;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/" element={<HomeRoute />} />
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <RegisterPage />
        </PublicRoute>
      } />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/payment/callback" element={<PaymentCallbackPage />} />
      <Route path="/forgot-password" element={
        <PublicRoute>
          <ForgotPassword />
        </PublicRoute>
      } />
      <Route path="/dashboard" element={
        <PrivateRoute>
          <BoardAdmin />
        </PrivateRoute>
      } />
      <Route path="/boards/:boardId/archived" element={
        <PrivateRoute>
          <ArchivedCards />
        </PrivateRoute>
      } />
      <Route path="/profile" element={<Navigate to="/account" replace />} />
      <Route path="/account" element={
        <PrivateRoute>
          <AccountManagementPage />
        </PrivateRoute>
      } />
      <Route path="/upgrade-plan" element={
        <PrivateRoute>
          <UpgradePlanPage />
        </PrivateRoute>
      } />
      <Route path="/api-tokens" element={
        <PrivateRoute>
          <ApiTokensManagement />
        </PrivateRoute>
      } />
      <Route path="/api-documentation" element={<ApiDocumentation />} />
      <Route path="/documentation" element={<UserDocumentation />} />
      <Route path="/cgu" element={<LegalPage type="cgu" />} />
      <Route path="/cgv" element={<LegalPage type="cgv" />} />
      <Route path="/privacy" element={<LegalPage type="privacy_policy" />} />
      <Route path="/cookies-policy" element={<LegalPage type="cookies_policy" />} />
      <Route path="/advertising-policy" element={<LegalPage type="advertising_policy" />} />
      <Route path="/admin/saas" element={
        <SuperAdminRoute>
          <UserAdminDashboard />
        </SuperAdminRoute>
      } />
      <Route path="/admin/saas/users/:userId" element={
        <SuperAdminRoute>
          <UserDetail />
        </SuperAdminRoute>
      } />
      <Route path="/admin/saas/plans" element={
        <SuperAdminRoute>
          <SubscriptionPlansConfig />
        </SuperAdminRoute>
      } />
      <Route path="/admin/saas/extended" element={
        <SuperAdminRoute>
          <AdminExtendedDashboard />
        </SuperAdminRoute>
      } />
      {/* Routes anciennes pour compatibilité */}
      <Route path="/admin/saas/tenants/:tenantId" element={
        <SuperAdminRoute>
          <TenantDetail />
        </SuperAdminRoute>
      } />
      <Route path="/join/:token" element={<JoinBoard />} />
      <Route path="/boards/:boardId" element={
        <PrivateRoute>
          <BoardPage />
        </PrivateRoute>
      } />
      <Route path="/boards" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
        <CookieBanner />
      </AuthProvider>
    </Router>
  );
};

export default App;
